import { useEffect, useRef, useState, type MouseEvent } from 'react';
import type { Episode } from '@/mocks/episodes';
import { STORAGE_KEYS, useLocalStorage } from '@/hooks/useLocalStorage';

interface PlayerProps {
  episode: Episode;
  dramaTitle: string;
}

const parseDuration = (value: string): number => {
  const parts = value.split(':').map((n) => Number(n));
  if (parts.length < 2 || parts.some((n) => Number.isNaN(n))) return 45 * 60;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return parts[0] * 60 + parts[1];
};

const formatTime = (seconds: number): string => {
  const total = Math.max(0, Math.floor(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

export default function Player({ episode, dramaTitle }: PlayerProps) {
  const duration = parseDuration(episode.duration);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [volume, setVolume] = useLocalStorage<number>(STORAGE_KEYS.volume, 70);
  const [error, setError] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const hideTimer = useRef<number | null>(null);

  useEffect(() => {
    setPlaying(false);
    setCurrent(0);
    setError(false);
  }, [episode.id]);

  useEffect(() => {
    if (!playing || error) return;
    const timer = window.setInterval(() => {
      setCurrent((prev) => {
        if (prev >= duration) {
          setPlaying(false);
          return duration;
        }
        return prev + 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [playing, error, duration]);

  const showControls = () => {
    setControlsVisible(true);
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setControlsVisible(false), 3200);
  };

  const progress = duration > 0 ? (current / duration) * 100 : 0;

  const handleSeek = (event: MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    setCurrent(Math.floor(ratio * duration));
  };

  const togglePlay = () => {
    if (error) return;
    setPlaying((p) => !p);
    showControls();
  };

  const skip = (delta: number) => {
    setCurrent((prev) => Math.min(duration, Math.max(0, prev + delta)));
    showControls();
  };

  return (
    <div
      className="relative w-full aspect-video rounded-lg overflow-hidden bg-primary-950 group"
      onMouseMove={showControls}
      onMouseLeave={() => setControlsVisible(false)}
    >
      <img
        src={episode.thumb}
        alt={`第${episode.number}集 播放画面`}
        title={`${dramaTitle} 第${episode.number}集 ${episode.title}`}
        className={`w-full h-full object-cover transition-opacity duration-500 ${playing ? 'opacity-80' : 'opacity-100'}`}
      />

      {!playing && !error && (
        <div className="absolute inset-0 bg-primary-950/45 flex items-center justify-center">
          <button
            type="button"
            onClick={togglePlay}
            aria-label="播放"
            className="w-20 h-20 rounded-full bg-accent-500 text-background-50 dark:text-foreground-950 flex items-center justify-center hover:bg-accent-600 hover:scale-105 transition-all animate-fade-in"
          >
            <i className="ri-play-fill text-4xl"></i>
          </button>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 bg-primary-950/85 flex flex-col items-center justify-center text-center px-6">
          <span className="w-14 h-14 rounded-full bg-accent-500/20 flex items-center justify-center text-accent-300 mb-4">
            <i className="ri-signal-wifi-error-line text-3xl"></i>
          </span>
          <h3 className="text-background-50 font-semibold">播放加载失败</h3>
          <p className="mt-2 text-sm text-background-300 max-w-sm">
            当前为播放演示，出现了模拟的加载异常。你可以点击下方按钮重试。
          </p>
          <button
            type="button"
            onClick={() => {
              setError(false);
              setCurrent(0);
            }}
            className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 dark:text-foreground-950 text-sm font-medium whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            重新加载
          </button>
        </div>
      )}

      {!error && (
        <div
          className={`absolute inset-x-0 bottom-0 p-3 md:p-4 bg-gradient-to-t from-primary-950/90 to-transparent transition-opacity duration-300 ${
            controlsVisible || !playing ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <div
            className="h-1.5 w-full rounded-full bg-background-50/25 cursor-pointer"
            onClick={handleSeek}
            role="presentation"
          >
            <div className="h-full rounded-full bg-accent-500 relative" style={{ width: `${progress}%` }}>
              <span className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-3 h-3 rounded-full bg-accent-500"></span>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-3 md:gap-4">
            <button type="button" onClick={togglePlay} aria-label={playing ? '暂停' : '播放'} className="text-background-50">
              <i className={`${playing ? 'ri-pause-fill' : 'ri-play-fill'} text-2xl`}></i>
            </button>
            <button type="button" onClick={() => skip(-10)} aria-label="后退10秒" className="text-background-100 hidden sm:block">
              <i className="ri-replay-10-line text-xl"></i>
            </button>
            <button type="button" onClick={() => skip(10)} aria-label="前进10秒" className="text-background-100 hidden sm:block">
              <i className="ri-forward-10-line text-xl"></i>
            </button>
            <span className="text-xs font-label text-background-100 tabular-nums">
              {formatTime(current)} / {formatTime(duration)}
            </span>
            <div className="ml-auto flex items-center gap-3">
              <span className="hidden sm:flex items-center gap-2">
                <i className="ri-volume-up-line text-background-100"></i>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={volume}
                  onChange={(e) => setVolume(Number(e.target.value))}
                  className="w-20 accent-accent-500"
                  aria-label="音量"
                />
              </span>
              <button
                type="button"
                onClick={() => setError(true)}
                className="text-[11px] text-background-200 border border-background-50/30 rounded-full px-2.5 py-1 whitespace-nowrap hover:text-accent-300 hover:border-accent-300 transition-colors"
              >
                模拟异常
              </button>
              <span className="text-background-100">
                <i className="ri-fullscreen-line text-xl"></i>
              </span>
            </div>
          </div>
        </div>
      )}

      <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-background-50/15 border border-background-50/30 text-background-50 text-[11px] backdrop-blur-sm">
        前端播放演示 · 无真实视频源
      </span>
    </div>
  );
}